'use client';

import { useQuery } from '@tanstack/react-query';
import { createPublicClient, http, type Hex } from 'viem';
import { base, baseSepolia } from 'viem/chains';

import {
  HV_TAKEOVER_ADAPTER_ADDRESS,
  V22_UGM_ADDRESSES,
} from '@/lib/constants/addresses';

const NO_REFOCUS = { refetchOnWindowFocus: false } as const;

const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const chain = chainId === '8453' ? base : baseSepolia;

/** V2.0 protocol-fee source UGM — kept as a candidate alongside any v2.2 entries. */
const V2_PROTOCOL_SOURCE_UGM = (process.env.NEXT_PUBLIC_V2_UGM_ADDRESS ||
  '0x50aa815b0FCd2686CD61d1bDd61188611f8367bF') as `0x${string}`;

/**
 * Canonical protocol-yield NFT hash wired into HV `pendingYield`; must match boardroom indexer + adapter wiring.
 *
 * Mirrors `takeover-app-classic/src/app/boardroom/page.tsx` — changing this breaks collectable totals.
 */
export const HV_PROTOCOL_ADAPTER_ASSET_HASH =
  '0x4586e541ec2ae8694e303762cfc777a34faa68e2dfdd0b9a00d4472b902810b1' as const;

export function boardroomFeesPublicClient() {
  return createPublicClient({ chain, transport: http('/api/rpc/proxy') });
}

const PENDING_YIELD_ABI = [
  {
    name: 'pendingYield',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '_assetHash', type: 'bytes32' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const NEXT_GRID_ID_ABI = [
  {
    name: 'nextGridId',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const PROTOCOL_FEE_RECEIVER_ABI = [
  {
    name: 'protocolFeeReceiver',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
] as const;

const TAX_REVENUE_ABI = [
  {
    name: 'taxRevenueUsdc',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const PROTOCOL_TAX_FEES_ABI = [
  {
    name: 'protocolTaxFees',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const PROTOCOL_SEAT_SALE_FEES_ABI = [
  {
    name: 'protocolSeatSaleFees',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

/**
 * `gridConfig(gridId)` — used only to read each grid's `taxToken` so we can
 * keep USDC-denominated fees and drop everything else. Identical tuple shape
 * across UGM v2.1 / v2.2 / v2.3 (`taxToken` is output index 4).
 */
const GRID_CONFIG_ABI = [
  {
    name: 'gridConfig',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [
      { name: 'creator', type: 'address' },
      { name: 'createdAt', type: 'uint64' },
      { name: 'totalSeats', type: 'uint16' },
      { name: 'taxRateBps', type: 'uint16' },
      { name: 'taxToken', type: 'address' },
      { name: 'yieldToken', type: 'address' },
    ],
  },
] as const;

/** Bytecode contains this selector ⇒ TSFM exposes `withdrawTaxRevenueForPools` (poke path). */
const WITHDRAW_TAX_NEW_SELECTOR = '2d51c853';

/** Response shape nested under `.data` from `GET /api/boardroom`. */
export interface BoardroomApiDataPayload {
  buybackStats?: {
    totalUsdcIn: string;
    totalTakeoverOut: string;
    totalBuybacks?: number;
  } | null;
  totalTileValueExBoardroom?: string;
  takeoverPriceUsdc?: string | null;
  managerAddresses?: string[];
  poolManagers?: Record<string, string>;
  v2PendingProtocolFees?: {
    pendingProtocolTaxFees: string;
    pendingProtocolSeatSaleFees: string;
  };
}

export interface TsfmManagerInfo {
  address: Hex;
  pending: bigint;
  usesNewAbi: boolean;
  poolIds: Hex[];
}

export interface CollectableFeesPayload {
  totalUsdc: bigint;
  adapterBalance: bigint;
  tsfmTotal: bigint;
  ugmTotal: bigint;
  oldManagers: TsfmManagerInfo[];
  newManagers: TsfmManagerInfo[];
  /**
   * UGM addresses that route protocol fees to the HV adapter, index-aligned with
   * `ugmGridIds`. Only grids with non-zero pending buckets are included so
   * `pokeAll` doesn't iterate dead grids.
   */
  ugms: Hex[];
  ugmGridIds: bigint[][];
}

/**
 * One source UGM whose `protocolFeeReceiver` matches the HV adapter — i.e.
 * sweeping it via `pokeAll` will land the USDC in the boardroom yield pool.
 */
export interface BoardroomFeeSourceUgm {
  address: Hex;
  gridIds: bigint[];
}

export function useBoardroomApiData(enabled = true) {
  return useQuery({
    queryKey: ['boardroom-api-data'],
    queryFn: async () => {
      const res = await fetch('/api/boardroom');
      if (!res.ok) throw new Error(`boardroom API ${res.status}`);
      const json = await res.json();
      return json.data as BoardroomApiDataPayload;
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
    ...NO_REFOCUS,
  });
}

/**
 * Build the de-duplicated, lower-cased list of UGM candidates the boardroom
 * keeper should consider sweeping: v2.0 source + every v2.2 entry the deploy
 * has been told about. Receiver-routing is verified separately on chain so
 * orphaned/migrated UGMs don't leak fees to the wrong address.
 */
function getCandidateUgms(): Hex[] {
  const seen = new Set<string>();
  const out: Hex[] = [];

  const push = (addr: string | undefined) => {
    if (!addr) return;
    const lower = addr.toLowerCase();
    if (!/^0x[0-9a-f]{40}$/.test(lower)) return;
    if (seen.has(lower)) return;
    seen.add(lower);
    out.push(lower as Hex);
  };

  push(V2_PROTOCOL_SOURCE_UGM);
  for (const a of V22_UGM_ADDRESSES) push(a);
  return out;
}

/**
 * List every UGM whose `protocolFeeReceiver` is the HV adapter, paired with
 * its full `[0..nextGridId)` grid array. UGMs whose receiver does NOT match
 * the HV adapter are dropped — sweeping them would route fees to the wrong
 * address (e.g. the v2.1 boardroom UGM points at a separate keeper EOA).
 */
export function useBoardroomFeeSourceUgms(enabled = true) {
  const candidates = getCandidateUgms();

  return useQuery({
    queryKey: ['hv-source-ugms', candidates, HV_TAKEOVER_ADAPTER_ADDRESS],
    queryFn: async (): Promise<{ ugms: BoardroomFeeSourceUgm[] }> => {
      if (candidates.length === 0) return { ugms: [] };

      const adapterLower = (HV_TAKEOVER_ADAPTER_ADDRESS || '').toLowerCase();
      if (!/^0x[0-9a-f]{40}$/.test(adapterLower)) return { ugms: [] };

      const client = boardroomFeesPublicClient();

      type Mcall = {
        address: Hex;
        abi: readonly unknown[];
        functionName: string;
      };
      const calls: Mcall[] = [];
      for (const ugm of candidates) {
        calls.push({
          address: ugm,
          abi: NEXT_GRID_ID_ABI,
          functionName: 'nextGridId',
        });
        calls.push({
          address: ugm,
          abi: PROTOCOL_FEE_RECEIVER_ABI,
          functionName: 'protocolFeeReceiver',
        });
      }

      const res = (await client.multicall({
        contracts: calls as never,
        allowFailure: true,
      })) as { status: 'success' | 'failure'; result?: unknown }[];

      const ugms: BoardroomFeeSourceUgm[] = [];
      for (let i = 0; i < candidates.length; i++) {
        const nextRes = res[i * 2];
        const receiverRes = res[i * 2 + 1];
        if (nextRes?.status !== 'success' || receiverRes?.status !== 'success') {
          continue;
        }
        const receiver = (receiverRes.result as string).toLowerCase();
        if (receiver !== adapterLower) {
          continue;
        }
        const nextId = nextRes.result as bigint;
        const gridIds: bigint[] = [];
        for (let g = 0n; g < nextId; g++) {
          gridIds.push(g);
        }
        ugms.push({ address: candidates[i], gridIds });
      }

      return { ugms };
    },
    enabled: enabled && candidates.length > 0 && !!HV_TAKEOVER_ADAPTER_ADDRESS,
    staleTime: 300_000,
    placeholderData: (prev) => prev,
    ...NO_REFOCUS,
  });
}

/**
 * @deprecated Use `useBoardroomFeeSourceUgms` — kept as a thin shim to avoid
 * breakage in any external import. Returns the first eligible UGM (typically
 * v2.0) so legacy single-UGM callers keep working.
 */
export function useBoardroomFeeSourceGridIds(enabled = true) {
  const q = useBoardroomFeeSourceUgms(enabled);
  return {
    ...q,
    data: q.data
      ? {
          ugmAddress: (q.data.ugms[0]?.address ?? V2_PROTOCOL_SOURCE_UGM) as Hex,
          gridIds: q.data.ugms[0]?.gridIds ?? ([] as bigint[]),
        }
      : undefined,
  } as const;
}

/**
 * Aggregate collectable protocol fees across:
 *   - the HV adapter's `pendingYield` (USDC sitting on the adapter)
 *   - every TSFM v1 manager's `taxRevenueUsdc()`
 *   - every UGM (v2.0 + v2.2) whose `protocolFeeReceiver` is the HV adapter,
 *     summing `protocolTaxFees(gid) + protocolSeatSaleFees(gid)` over its grids
 *
 * Returns `ugms` and `ugmGridIds` index-aligned with grids pruned to those
 * with non-zero pending balance, so `pokeAll` only touches grids that need
 * sweeping.
 */
export function useCollectableProtocolFees(
  sourceUgms: { ugms: BoardroomFeeSourceUgm[] } | undefined,
  boardroomApiData: BoardroomApiDataPayload | undefined,
  enabled = true,
) {
  const managerAddresses = boardroomApiData?.managerAddresses;
  const poolManagers = boardroomApiData?.poolManagers;
  const ugmAddrs = sourceUgms?.ugms.map((u) => u.address) ?? [];

  return useQuery({
    queryKey: ['hv-collectable-fees', ugmAddrs, managerAddresses],
    queryFn: async (): Promise<CollectableFeesPayload> => {
      const client = boardroomFeesPublicClient();
      const adapterAddr = HV_TAKEOVER_ADAPTER_ADDRESS as Hex;
      if (!adapterAddr || !/^0x[0-9a-fA-F]{40}$/.test(adapterAddr)) {
        throw new Error('HV_TAKEOVER_ADAPTER_ADDRESS not configured');
      }

      const assetHash = HV_PROTOCOL_ADAPTER_ASSET_HASH as Hex;
      const ugms = sourceUgms?.ugms ?? [];

      const managerToPoolIds = new Map<string, Hex[]>();
      for (const [pId, mAddr] of Object.entries(poolManagers ?? {})) {
        const key = mAddr.toLowerCase();
        if (!managerToPoolIds.has(key)) managerToPoolIds.set(key, []);
        managerToPoolIds.get(key)!.push(pId as Hex);
      }

      const managers = managerAddresses ?? [];
      type Mcall = {
        address: Hex;
        abi: readonly unknown[];
        functionName: string;
        args?: readonly unknown[];
      };

      const calls: Mcall[] = [
        {
          address: adapterAddr,
          abi: PENDING_YIELD_ABI,
          functionName: 'pendingYield',
          args: [assetHash],
        },
      ];

      for (const addr of managers) {
        calls.push({
          address: addr as Hex,
          abi: TAX_REVENUE_ABI,
          functionName: 'taxRevenueUsdc',
        });
      }

      // Index where each UGM's `protocolTaxFees` / `protocolSeatSaleFees` /
      // `gridConfig` block starts, so we can re-associate results back to
      // (ugm, gridId).
      const ugmRanges: {
        taxStart: number;
        seatStart: number;
        cfgStart: number;
        gridIds: bigint[];
      }[] = [];
      const tsfmStartIdx = 1;
      const ugmReadStart = tsfmStartIdx + managers.length;
      let cursor = ugmReadStart;
      for (const u of ugms) {
        const taxStart = cursor;
        for (const gid of u.gridIds) {
          calls.push({
            address: u.address,
            abi: PROTOCOL_TAX_FEES_ABI,
            functionName: 'protocolTaxFees',
            args: [gid],
          });
        }
        cursor += u.gridIds.length;
        const seatStart = cursor;
        for (const gid of u.gridIds) {
          calls.push({
            address: u.address,
            abi: PROTOCOL_SEAT_SALE_FEES_ABI,
            functionName: 'protocolSeatSaleFees',
            args: [gid],
          });
        }
        cursor += u.gridIds.length;
        const cfgStart = cursor;
        for (const gid of u.gridIds) {
          calls.push({
            address: u.address,
            abi: GRID_CONFIG_ABI,
            functionName: 'gridConfig',
            args: [gid],
          });
        }
        cursor += u.gridIds.length;
        ugmRanges.push({ taxStart, seatStart, cfgStart, gridIds: u.gridIds });
      }

      const multicallResults = await client.multicall({
        contracts: calls as never,
        allowFailure: true,
      });

      const res = multicallResults as {
        status: 'success' | 'failure';
        result?: unknown;
      }[];

      const adapterBalance =
        res[0]?.status === 'success' ? (res[0].result as bigint) : 0n;

      const tsfmPending = managers.map((_, i) => {
        const r = res[tsfmStartIdx + i];
        return r?.status === 'success' ? (r.result as bigint) : 0n;
      });

      const managersWithPending = managers
        .map((addr, i) => ({ addr, pending: tsfmPending[i], idx: i }))
        .filter((m) => m.pending > 0n);

      const codeResults =
        managersWithPending.length > 0
          ? await Promise.all(
              managersWithPending.map((m) =>
                client.getCode({ address: m.addr as Hex }),
              ),
            )
          : [];

      const codeMap = new Map<number, string>();
      managersWithPending.forEach((m, ci) => {
        codeMap.set(m.idx, codeResults[ci] ?? '');
      });

      const tsfmInfos: TsfmManagerInfo[] = managers.map((addr, i) => {
        const code = codeMap.get(i) ?? '';
        return {
          address: addr as Hex,
          pending: tsfmPending[i],
          usesNewAbi: code.toLowerCase().includes(WITHDRAW_TAX_NEW_SELECTOR),
          poolIds: managerToPoolIds.get(addr.toLowerCase()) ?? [],
        };
      });

      const tsfmTotal = tsfmPending.reduce((sum, p) => sum + p, 0n);

      // The boardroom yield token — and therefore the only fee denomination
      // `_flushYield` can push as yield — is USDC. A grid whose `taxToken` is
      // anything else (e.g. TAKEOVER, 18-decimals) accrues protocol fees in
      // THAT token; summing those raw base units into a 6-decimal USDC total
      // produces an astronomically large mixed number on the "Collect Taxes"
      // button. Worse, `pokeAll` would sweep those non-USDC fees into the
      // ProtocolYieldAdapter, which can only flush USDC and has no rescue
      // path — so they'd be stranded. Restrict the total AND the pokeAll
      // grid lists to USDC-tax grids only.
      const usdcLower = (process.env.NEXT_PUBLIC_USDC_ADDRESS || '').toLowerCase();
      let ugmTotal = 0n;
      const prunedUgms: Hex[] = [];
      const prunedGridIds: bigint[][] = [];
      for (let i = 0; i < ugms.length; i++) {
        const range = ugmRanges[i];
        const gridIds = range.gridIds;
        const keep: bigint[] = [];
        for (let j = 0; j < gridIds.length; j++) {
          const cfg = res[range.cfgStart + j];
          const taxToken =
            cfg?.status === 'success'
              ? (((cfg.result as readonly unknown[])?.[4] as string) || '').toLowerCase()
              : '';
          // Drop non-USDC grids: their fees are a different token/decimals and
          // must not contaminate the USD total or be swept into the adapter.
          if (!usdcLower || taxToken !== usdcLower) continue;

          const tax = res[range.taxStart + j];
          const seat = res[range.seatStart + j];
          const taxV = tax?.status === 'success' ? (tax.result as bigint) : 0n;
          const seatV = seat?.status === 'success' ? (seat.result as bigint) : 0n;
          const sum = taxV + seatV;
          if (sum > 0n) {
            keep.push(gridIds[j]);
            ugmTotal += sum;
          }
        }
        if (keep.length > 0) {
          prunedUgms.push(ugms[i].address);
          prunedGridIds.push(keep);
        }
      }

      return {
        totalUsdc: adapterBalance + tsfmTotal + ugmTotal,
        adapterBalance,
        tsfmTotal,
        ugmTotal,
        oldManagers: tsfmInfos.filter((m) => !m.usesNewAbi && m.pending > 0n),
        newManagers: tsfmInfos.filter((m) => m.usesNewAbi && m.pending > 0n),
        ugms: prunedUgms,
        ugmGridIds: prunedGridIds,
      };
    },
    enabled:
      !!enabled &&
      !!HV_TAKEOVER_ADAPTER_ADDRESS &&
      !!sourceUgms &&
      sourceUgms.ugms.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
    ...NO_REFOCUS,
  });
}
